/**
 * Utility functions for JWT token handling
 */

interface DecodedToken {
  exp: number;
  iat: number;
  sub: string;
  organization_id?: string;
  [key: string]: unknown;
}

/**
 * Safely decode a JWT token payload
 * @param token JWT token string
 * @returns Decoded token payload or null if invalid
 */
export function decodeToken(token: string): DecodedToken | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * @param token JWT token string
 * @returns true if token is expired, false otherwise
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true; // Treat invalid tokens as expired
  }

  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
}

/**
 * Get the expiry date of a JWT token
 * @param token JWT token string
 * @returns Date object or null if invalid
 */
export function getTokenExpiry(token: string): Date | null {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return null;
  }

  return new Date(decoded.exp * 1000);
}

/**
 * Check if a JWT token is near expiry (within specified buffer time)
 * @param token JWT token string
 * @param bufferMinutes Minutes before expiry to consider "near expiry" (default: 60)
 * @returns true if token expires within the buffer time
 */
export function isTokenNearExpiry(token: string, bufferMinutes: number = 60): boolean {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true; // Treat invalid tokens as near expiry
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const bufferSeconds = bufferMinutes * 60;
  return decoded.exp < (currentTime + bufferSeconds);
}

/**
 * Get the organization ID from a JWT token
 * @param token JWT token string
 * @returns Organization ID or null if not found
 */
export function getOrganizationIdFromToken(token: string): string | null {
  const decoded = decodeToken(token);
  return decoded?.organization_id || null;
}

/**
 * Get the user ID from a JWT token
 * @param token JWT token string
 * @returns User ID or null if not found
 */
export function getUserIdFromToken(token: string): string | null {
  const decoded = decodeToken(token);
  return decoded?.sub || null;
}

/**
 * Get token issued at time
 * @param token JWT token string
 * @returns Date object or null if invalid
 */
export function getTokenIssuedAt(token: string): Date | null {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.iat) {
    return null;
  }

  return new Date(decoded.iat * 1000);
}

/**
 * Check if a token is valid (not expired and properly formatted)
 * @param token JWT token string
 * @returns true if token is valid and not expired
 */
export function isTokenValid(token: string): boolean {
  if (!token) return false;

  const decoded = decodeToken(token);
  if (!decoded) return false;

  return !isTokenExpired(token);
}